import {
  computed,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  ConcerningAreaType,
  isWidgetStructureObject,
  ModuleStructureObject,
  SectionStructureObject,
  SubSectionStructureObject,
  WidgetMappedType,
  WidgetStructureObject,
} from '../../api/interfaces';
import { createPointsModel } from '../points/points-model';
import { extractRuleIds } from '../rules/rule-compiler';
import { UniqueIdService } from './unique-id.service';
import { defaultStructure } from './default-model';

@Injectable()
export class BuilderService {
  private readonly uniqueIdService = inject(UniqueIdService);

  private readonly _initialStructure: ModuleStructureObject = {
    root: { _type: 'root', children: [] },
  };

  readonly tenderFormInfo = signal<
    | {
        id: string;
        revision?: number;
        revisions?: { [key: number]: string }[];
        tenderId: string;
        title: string;
      }
    | undefined
  >(undefined);

  readonly moduleStructure: WritableSignal<ModuleStructureObject> = signal(
    this._initialStructure,
  );
  readonly tenderFormId = signal<string | undefined>(undefined);

  readonly errors = computed(() => {
    const structure = this.moduleStructure();
    const errors: Record<
      string,
      { missingIds: string[]; type: 'section' | 'subsection' | 'widget' }
    > = {};

    const allIds = new Set(Object.keys(structure));
    Object.entries(structure).forEach(([uniqueId, node]) => {
      if (node._type === 'root' || node._type === 'section') return;

      const missingIds = extractRuleIds(node.customization.showIfRule);

      errors[uniqueId] = {
        type: node._type as 'section' | 'subsection' | 'widget',
        missingIds: missingIds.filter((id) => !allIds.has(id)),
      };
    });
    return errors;
  });

  private readonly points = createPointsModel(this.moduleStructure);

  readonly widgetTotalWeights = this.points.widgetTotalWeights;
  readonly subsectionWidgets = this.points.subsectionWidgets;
  readonly sectionsPoints = this.points.sectionsPoints;
  readonly subsectionsPoints = this.points.subsectionsPoints;
  readonly sectionUsedPoints = this.points.sectionUsedPoints;
  readonly subsectionWeights = this.points.subsectionWeights;

  constructor() {
    this.uniqueIdService.moduleStructure = this.moduleStructure;
  }

  getMaxPointsForWidget(widgetId: string) {
    return this.points.maxPointsForWidget(widgetId);
  }

  getWeightedPointsForWidget(
    widgetId: string,
    weight: number | undefined | null,
  ) {
    return this.points.weightedPointsForWidget(widgetId, weight);
  }

  resetModuleStructure() {
    this.moduleStructure.set(this._initialStructure);
  }

  addSection() {
    const structure = this.moduleStructure();
    const node: SectionStructureObject = {
      _type: 'section',
      children: [],
      customization: {
        title: 'Sezione',
        subTitle: '',
        description: '',
        points: 0,
      },
    };
    const nodeId = this.uniqueIdService.getUniqueId(node._type);
    structure[nodeId] = node;
    structure['root'].children.push(nodeId);

    const subNode: SubSectionStructureObject = {
      _type: 'subsection',
      children: [],
      customization: {
        title: 'Sottosezione',
        description: '',
        subTitle: '',
        showIfRule: '',
        points: 0,
        concerningArea: null,
      },
    };
    const subNodeId = this.uniqueIdService.getUniqueId(subNode._type);
    structure[subNodeId] = subNode;
    structure[nodeId].children.push(subNodeId);

    this.moduleStructure.set({ ...structure });
  }

  removeSection(sectionId: string) {
    const structure = this.moduleStructure();

    structure['root'].children = structure['root'].children.filter(
      (id) => id !== sectionId,
    );

    const section = structure[sectionId] as SectionStructureObject;
    section.children.forEach((subSectionId) => {
      const subSection = structure[subSectionId] as SubSectionStructureObject;
      subSection.children.forEach((widgetId) => {
        delete structure[widgetId];
      });
      delete structure[subSectionId];
    });

    delete structure[sectionId];

    this.moduleStructure.set({ ...structure });
  }

  moveSection(previousIndex: number, currentIndex: number) {
    const structure = this.moduleStructure();
    const sectionId = structure['root'].children[previousIndex];
    structure['root'].children.splice(previousIndex, 1);
    structure['root'].children.splice(currentIndex, 0, sectionId);
    this.moduleStructure.set({ ...structure });
  }

  addSubsection(sectionId: string) {
    const structure = this.moduleStructure();
    const node: SubSectionStructureObject = {
      _type: 'subsection',
      children: [],
      customization: {
        title: 'Sottosezione',
        description: '',
        subTitle: '',
        showIfRule: '',
        points: 0,
        concerningArea: null,
      },
    };

    const nodeId = this.uniqueIdService.getUniqueId(node._type);

    structure[nodeId] = node;
    const section = structure[sectionId] as SectionStructureObject;
    section.children.push(nodeId);

    this.moduleStructure.set({ ...structure });
  }

  removeSubsection(sectionId: string, subSectionId: string) {
    const structure = this.moduleStructure();
    const section = structure[sectionId] as SectionStructureObject;
    section.children = section.children.filter((id) => id !== subSectionId);
    const subsectionChildren = structure[
      subSectionId
    ] as SubSectionStructureObject;
    subsectionChildren.children.forEach((widgetId) => {
      delete structure[widgetId];
    });
    delete structure[subSectionId];
    this.moduleStructure.set({ ...structure });
  }

  moveSubsection(
    sectionId: string,
    previousContainerId: string,
    subSectionId: string,
    index: number,
  ) {
    const structure = this.moduleStructure();
    const previousSection = structure[
      previousContainerId
    ] as SectionStructureObject;
    previousSection.children = previousSection.children.filter(
      (id) => id !== subSectionId,
    );

    (structure[sectionId] as SectionStructureObject).children.splice(
      index,
      0,
      subSectionId,
    );

    this.moduleStructure.set({ ...structure });
  }

  addWidget(
    subsectionId: string,
    widget: WidgetStructureObject,
    index?: number,
  ) {
    const structure = this.moduleStructure();
    const nodeId = this.uniqueIdService.getUniqueId(widget._type);
    structure[nodeId] = widget;
    const subsection = structure[subsectionId] as SubSectionStructureObject;
    if (index !== undefined) {
      subsection.children.splice(index, 0, nodeId);
    } else {
      subsection.children.push(nodeId);
    }
    this.moduleStructure.set({ ...structure });
  }

  removeWidget(subsectionId: string, widgetId: string) {
    const structure = this.moduleStructure();
    const subsection = structure[subsectionId] as SubSectionStructureObject;
    subsection.children = subsection.children.filter((id) => id !== widgetId);
    delete structure[widgetId];
    this.moduleStructure.set({ ...structure });
  }

  addBlock(
    block: Record<
      string,
      SectionStructureObject | SubSectionStructureObject | WidgetStructureObject
    >,
    fatherId: 'root' | string,
  ) {
    const structure = this.moduleStructure();
    Object.entries(block).forEach(([key, value]) => {
      structure[key] = value;
    });

    const father = structure[fatherId];
    if (!isWidgetStructureObject(father)) {
      const fatherType = father._type;
      const newChildId = Object.entries(block).find((_) => {
        if (fatherType === 'root') return _[1]._type === 'section';
        if (fatherType === 'section') return _[1]._type === 'subsection';
        if (fatherType === 'subsection') return _[1]._type === 'widget';
        return false;
      });

      if (newChildId) {
        father.children.push(newChildId[0]);
      }
    }

    structure[fatherId] = father;

    this.moduleStructure.set({ ...structure });
  }

  updateSection(
    oldSectionId: string,
    newSectionId: string,
    customization: {
      title: string;
      subTitle: string;
      description: string;
      points: number;
    },
  ) {
    const structure = this.moduleStructure();
    const newSection = {
      ...structure[oldSectionId],
    } as SectionStructureObject;
    delete structure[oldSectionId];

    structure[newSectionId] = {
      ...newSection,
      customization: customization,
    };

    const root = structure['root'];
    root.children = root.children.map((id) =>
      id === oldSectionId ? newSectionId : id,
    );

    this.moduleStructure.set({ ...structure });
  }

  updateSubsection(
    sectionId: string,
    oldSubsectionId: string,
    newSubsectionId: string,
    subsectionData: {
      title: string;
      description: string;
      subTitle: string;
      showIfRule: string;
      points: number;
      concerningArea: ConcerningAreaType | null;
    },
  ) {
    const structure = this.moduleStructure();

    const newSubsection = {
      ...structure[oldSubsectionId],
    } as SubSectionStructureObject;

    delete structure[oldSubsectionId];

    structure[newSubsectionId] = {
      ...newSubsection,
      customization: subsectionData,
    };

    const section = structure[sectionId] as SectionStructureObject;
    section.children = section.children.map((id) =>
      id === oldSubsectionId ? newSubsectionId : id,
    );

    Object.keys(structure).forEach((key) => {
      const node = structure[key];
      if (node._type === 'root' || node._type === 'section') return;
      const showIfRule = node.customization.showIfRule;
      const updatedRule = showIfRule.replace(
        new RegExp(`\\$${oldSubsectionId}\\$`, 'g'),
        `$${newSubsectionId}$`,
      );
      node.customization.showIfRule = updatedRule;
    });

    this.moduleStructure.set({ ...structure });
  }

  updateWidget(
    sectionId: string,
    oldWidgetId: string,
    newWidgetId: string,
    widgetData: unknown,
    mappedType: WidgetMappedType,
  ) {
    const structure = this.moduleStructure();
    delete structure[oldWidgetId];

    structure[newWidgetId] = {
      _type: 'widget',
      _mappedType: mappedType,
      customization: widgetData,
    } as WidgetStructureObject;

    const subsection = structure[sectionId] as SubSectionStructureObject;
    subsection.children = subsection.children.map((id) =>
      id === oldWidgetId ? newWidgetId : id,
    );

    Object.keys(structure).forEach((key) => {
      const node = structure[key];
      if (node._type === 'root' || node._type === 'section') return;
      const showIfRule = node.customization.showIfRule;
      const updatedRule = showIfRule.replace(
        new RegExp(`\\$${oldWidgetId}\\$`, 'g'),
        `$${newWidgetId}$`,
      );
      node.customization.showIfRule = updatedRule;
    });

    this.moduleStructure.set({ ...structure });
  }

  moveWidget(
    previousContainerId: string,
    subsectionId: string,
    widgetId: string,
    index: number,
  ) {
    const structure = this.moduleStructure();
    const previousSubsection = structure[
      previousContainerId
    ] as SubSectionStructureObject;

    previousSubsection.children = previousSubsection.children.filter(
      (id) => id !== widgetId,
    );

    (structure[subsectionId] as SubSectionStructureObject).children.splice(
      index,
      0,
      widgetId,
    );
    this.moduleStructure.set({ ...structure });
  }
}
